// src/marketing/MarketingDashboardPage.tsx
import { useMemo } from 'react'
import { useAppStore } from '../shell/appStore'
import { useLaunchPlans } from './launch/hooks/useLaunchPlans'
import { useLaunchTasks } from './launch/hooks/useLaunchTasks'
import { useBudgetLines } from './channels/hooks/useBudgetLines'
import { useDailyExpenses } from './budget/useDailyExpenses'
import { actualByCategory } from './budget/aggregate'
import { useStatsSummary, useAppIntegrations } from '../stats/api'
import { launchTaskDate } from './launch/dates'
import { KpiCard } from '../stats/KpiCard'
import type { LaunchPlan, LaunchTask } from '../lib/types'

// Date du jour — figée au chargement du module pour les calculs de retard
const TODAY = new Date().toISOString().slice(0, 10)

/**
 * Calcule la fenêtre de stats autour du lancement.
 * Si launchDate est défini : [D0-30, D0+30].
 * Sinon : [aujourd'hui-30, aujourd'hui].
 */
function statsWindow(launchDate: string | null): { from: string; to: string } {
  if (launchDate) {
    const d0 = new Date(launchDate)
    const from = new Date(d0)
    from.setUTCDate(from.getUTCDate() - 30)
    const to = new Date(d0)
    to.setUTCDate(to.getUTCDate() + 30)
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    }
  }
  const to = new Date()
  const from = new Date()
  from.setUTCDate(from.getUTCDate() - 30)
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  }
}

// ── Hero éditorial ────────────────────────────────────────────────────────

function DashboardHero() {
  return (
    <header
      data-testid="dashboard-hero"
      style={{
        borderBottom: '1px solid var(--line-strong)',
        paddingBottom: '2rem',
        marginBottom: '2rem',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '10px',
          letterSpacing: '.18em',
          color: 'var(--bronze)',
          textTransform: 'uppercase',
          marginBottom: '10px',
        }}
      >
        Marketing
      </div>
      <h1
        style={{
          fontWeight: 800,
          fontVariationSettings: '"wdth" 118',
          fontSize: 'clamp(22px, 3vw, 34px)',
          textTransform: 'uppercase',
          letterSpacing: '.005em',
          lineHeight: 1.05,
          margin: '0 0 8px',
          color: 'var(--fg)',
        }}
      >
        Tableau de bord
      </h1>
      <p
        style={{
          fontFamily: 'var(--mono)',
          fontSize: '12px',
          color: 'var(--dim)',
          letterSpacing: '.04em',
          margin: 0,
        }}
      >
        Vue d'ensemble — lancement, budget, KPIs
      </p>
    </header>
  )
}

// ── Sous-composant : progression ──────────────────────────────────────────

function LaunchProgressSection({
  plan,
  tasks,
}: {
  plan: LaunchPlan
  tasks: LaunchTask[]
}) {
  const total = tasks.length
  const done = tasks.filter((t) => t.done).length
  const pct = total === 0 ? 0 : Math.round((done / total) * 100)

  return (
    <section
      data-testid="launch-progress"
      style={{
        border: '1px solid var(--line-strong)',
        marginBottom: '1.5rem',
      }}
    >
      {/* En-tête en tuile */}
      <div className="tiles" style={{ borderBottom: '1px solid var(--line-strong)', marginTop: 0 }}>
        <div className="tile">
          <div className="k">Progression</div>
          <div className="v">
            {pct}%
            <small>{done} / {total} tâches</small>
          </div>
        </div>
        <div className="tile">
          <div className="k">Plan</div>
          <div className="v" style={{ fontSize: '14px', textTransform: 'none', letterSpacing: 0 }}>
            {plan.name}
            {plan.launchDate && <small>Lancement : {plan.launchDate}</small>}
          </div>
        </div>
      </div>

      {/* Barre de progression */}
      <div style={{ padding: '1rem 1.25rem' }}>
        <div
          role="progressbar"
          data-testid="launch-progress-bar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Progression : ${pct}%`}
          style={{
            height: '6px',
            background: 'var(--elevated)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: pct === 100 ? '#4caf7a' : 'var(--arancio)',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    </section>
  )
}

// ── Sous-composant : prochaines tâches ────────────────────────────────────

function UpcomingTasksSection({
  plan,
  tasks,
}: {
  plan: LaunchPlan
  tasks: LaunchTask[]
}) {
  const upcoming = tasks
    .filter((t) => !t.done)
    .map((t) => ({
      ...t,
      calculatedDate: launchTaskDate(plan.launchDate, t.dayOffset),
    }))
    .sort((a, b) => {
      if (a.calculatedDate && b.calculatedDate)
        return a.calculatedDate.localeCompare(b.calculatedDate)
      if (a.calculatedDate) return -1
      if (b.calculatedDate) return 1
      return a.milestoneOrder - b.milestoneOrder || a.sortOrder - b.sortOrder
    })
    .slice(0, 8)

  return (
    <section
      data-testid="upcoming-tasks"
      style={{
        border: '1px solid var(--line-strong)',
        marginBottom: '1.5rem',
      }}
    >
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--line-strong)',
          background: 'var(--surface)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '10px',
            letterSpacing: '.18em',
            color: 'var(--bronze)',
            textTransform: 'uppercase',
          }}
        >
          Prochaines tâches
        </span>
      </div>

      <div style={{ padding: '1rem 1.25rem' }}>
        {upcoming.length === 0 ? (
          <p style={{ fontSize: '0.875rem', color: 'var(--dim)', margin: 0 }}>
            Toutes les tâches sont complétées.
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            {upcoming.map((task) => {
              const isLate =
                task.calculatedDate !== null && task.calculatedDate < TODAY
              return (
                <li
                  key={task.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    background: isLate ? 'rgba(217,83,30,0.06)' : 'var(--elevated)',
                    border: `1px solid ${isLate ? 'rgba(217,83,30,0.25)' : 'transparent'}`,
                    gap: '0.75rem',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        color: 'var(--fg)',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.title}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--dim)' }}>
                      {task.milestone}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flexShrink: 0,
                    }}
                  >
                    {task.calculatedDate && (
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: isLate ? 'var(--arancio)' : 'var(--dim)',
                          fontFamily: 'var(--mono)',
                        }}
                      >
                        {task.calculatedDate}
                      </span>
                    )}
                    {isLate && (
                      <span
                        style={{
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: 'var(--arancio)',
                          background: 'rgba(217,83,30,0.1)',
                          border: '1px solid rgba(217,83,30,0.3)',
                          padding: '0.125rem 0.375rem',
                        }}
                      >
                        retard
                      </span>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}

// ── Sous-composant : résumé budget ────────────────────────────────────────

function BudgetSummarySection({
  totalPlanned,
  totalActual,
  currency,
}: {
  totalPlanned: number
  totalActual: number
  currency: string
}) {
  const ratio = totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0
  const overBudget = totalActual > totalPlanned

  return (
    <section
      data-testid="budget-summary"
      style={{
        border: '1px solid var(--line-strong)',
        marginBottom: '1.5rem',
      }}
    >
      <div className="tiles" style={{ marginTop: 0 }}>
        <div className="tile">
          <div className="k">Planifié</div>
          <div className="v">
            {totalPlanned.toLocaleString('fr-FR')}
            <small>{currency}</small>
          </div>
        </div>
        <div className="tile">
          <div className="k">Réalisé</div>
          <div className="v" style={{ color: overBudget ? 'var(--arancio)' : undefined }}>
            {totalActual.toLocaleString('fr-FR')}
            <small>{currency}</small>
          </div>
        </div>
        <div className="tile">
          <div className="k">Consommé</div>
          <div className="v" style={{ color: overBudget ? 'var(--arancio)' : '#4caf7a' }}>
            {ratio.toFixed(0)} %
          </div>
        </div>
      </div>

      {/* Barre de consommation */}
      <div style={{ padding: '0 0 1px' }}>
        <div
          style={{
            height: '3px',
            background: 'var(--elevated)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.min(ratio, 100)}%`,
              background: overBudget ? 'var(--arancio)' : '#4caf7a',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    </section>
  )
}

// ── Sous-composant : KPIs Stats ───────────────────────────────────────────

function KpiStatsSection({
  appId,
  statsFrom,
  statsTo,
}: {
  appId: string
  statsFrom: string
  statsTo: string
}) {
  const { data: integrations = [], isLoading: loadingInt } =
    useAppIntegrations(appId)
  const { data: summary, isLoading: loadingSummary } = useStatsSummary(
    appId,
    statsFrom,
    statsTo,
  )

  const hasConfigured = integrations.some((i) => i.enabled)

  if (loadingInt) {
    return (
      <section
        data-testid="kpi-section"
        style={{
          border: '1px solid var(--line-strong)',
        }}
      >
        <div style={{ padding: '1.25rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
          Chargement des KPIs…
        </div>
      </section>
    )
  }

  if (!hasConfigured) {
    return (
      <section
        data-testid="kpi-not-configured"
        style={{
          border: '1px solid var(--line-strong)',
        }}
      >
        <div className="tiles" style={{ marginTop: 0 }}>
          <div className="tile">
            <div className="k">KPIs</div>
            <div className="v" style={{ fontSize: '13px', textTransform: 'none', fontWeight: 500, letterSpacing: 0 }}>
              <small style={{ display: 'block', marginTop: 0, fontSize: '12px', color: 'var(--dim)' }}>
                Configurez vos intégrations Stats (App Store Connect / RevenueCat)
                pour voir les KPIs ici.
              </small>
            </div>
          </div>
        </div>
      </section>
    )
  }

  const downloads = summary?.metrics['new_downloads']?.current ?? 0
  const mrr = summary?.metrics['mrr']?.current ?? 0
  const ratingAvg = summary?.metrics['rating_avg']?.current ?? 0

  return (
    <section
      data-testid="kpi-section"
      style={{
        border: '1px solid var(--line-strong)',
      }}
    >
      {loadingSummary ? (
        <div style={{ padding: '1.25rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
          Chargement…
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '0.75rem',
            padding: '1rem',
          }}
        >
          <KpiCard
            metric="new_downloads"
            label="Téléchargements"
            current={downloads}
            previous={summary?.metrics['new_downloads']?.previous ?? null}
            notConfigured={
              !integrations.some(
                (i) => i.source === 'app_store_connect' && i.enabled,
              )
            }
          />
          <KpiCard
            metric="mrr"
            label="MRR"
            current={mrr}
            previous={summary?.metrics['mrr']?.previous ?? null}
            notConfigured={
              !integrations.some(
                (i) => i.source === 'revenuecat' && i.enabled,
              )
            }
          />
          <KpiCard
            metric="rating_avg"
            label="Note moyenne"
            current={ratingAvg}
            previous={summary?.metrics['rating_avg']?.previous ?? null}
            notConfigured={
              !integrations.some(
                (i) => i.source === 'app_store_connect' && i.enabled,
              )
            }
          />
        </div>
      )}
    </section>
  )
}

// ── Page principale ───────────────────────────────────────────────────────

export function MarketingDashboardPage() {
  const appId = useAppStore((s) => s.activeAppId)

  const { data: plans = [], isLoading: loadingPlans } = useLaunchPlans(appId)
  const activePlan =
    plans.find((p) => p.status === 'active') ?? plans[0] ?? null

  const { data: tasks = [], isLoading: loadingTasks } = useLaunchTasks(
    activePlan?.id ?? null,
  )

  const { data: budgetLines = [], isLoading: loadingBudget } =
    useBudgetLines(appId)

  const { data: expenses = [] } = useDailyExpenses(appId)
  const computedActuals = useMemo(() => actualByCategory(expenses), [expenses])

  const { from: statsFrom, to: statsTo } = statsWindow(
    activePlan?.launchDate ?? null,
  )

  if (!appId) {
    return (
      <div
        style={{
          padding: '2rem',
          color: 'var(--muted)',
          fontSize: '0.875rem',
        }}
      >
        Sélectionnez une app pour voir le tableau de bord Marketing.
      </div>
    )
  }

  const totalPlanned = budgetLines.reduce((acc, l) => acc + l.planned, 0)
  const totalActual = [...new Set(budgetLines.map((l) => l.category))].reduce(
    (sum, cat) => sum + (computedActuals.get(cat) ?? 0),
    0,
  )
  const currency = budgetLines[0]?.currency ?? 'EUR'

  const isLoading = loadingPlans || loadingTasks || loadingBudget

  return (
    <div
      data-testid="marketing-dashboard"
      style={{ padding: '1.5rem 2rem', maxWidth: '900px' }}
    >
      <DashboardHero />

      {isLoading ? (
        <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
          Chargement…
        </div>
      ) : (
        <>
          {activePlan ? (
            <>
              <LaunchProgressSection plan={activePlan} tasks={tasks} />
              <UpcomingTasksSection plan={activePlan} tasks={tasks} />
            </>
          ) : (
            <section
              data-testid="launch-progress"
              style={{
                border: '1px solid var(--line-strong)',
                marginBottom: '1.5rem',
              }}
            >
              <div className="tiles" style={{ marginTop: 0 }}>
                <div className="tile">
                  <div className="k">Lancement</div>
                  <div className="v" style={{ fontSize: '13px', textTransform: 'none', fontWeight: 500, letterSpacing: 0 }}>
                    <small>
                      Aucun plan de lancement actif.{' '}
                      <a href="/marketing/launch" style={{ color: 'var(--arancio)' }}>
                        Créer un plan
                      </a>
                    </small>
                  </div>
                </div>
              </div>
            </section>
          )}

          <BudgetSummarySection
            totalPlanned={totalPlanned}
            totalActual={totalActual}
            currency={currency}
          />

          <KpiStatsSection
            appId={appId}
            statsFrom={statsFrom}
            statsTo={statsTo}
          />
        </>
      )}
    </div>
  )
}
